import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import classNames from 'classnames';

import uniqueId from '../../../utils/uniqueId';
import DeepValue from '../../decorators/DeepValue';
import ChangeDetection from '../../decorators/ChangeDetection';

import style from './style.module.css';

const modules = {
  toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['link']],
};

const formats = ['bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];

const Wysiwyg = ({
  id = uniqueId(),
  label,
  name,
  onChange,
  placeholder,
  showLabel = false,
  value,
  changed = false,
  className,
  ...rest
}) => {
  const [text, setText] = useState(value);
  const quillRef = useRef(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  const handlePaste = useCallback(event => {
    const paste = (event.clipboardData || window.clipboardData).getData('text');
    const selection = window.getSelection();

    if (selection.rangeCount > 0) {
      selection.getRangeAt(0).insertNode(document.createTextNode(paste));
      event.preventDefault();
    }
  }, []);

  const handleTabKeyUp = useCallback(event => {
    if (event.key === 'Tab' && event.target.classList.contains('ql-container')) {
      if (quillRef.current) {
        quillRef.current.focus();
      }
    }
  }, []);

  const handleChange = useCallback(
    value => {
      setText(value);

      if (onChange) {
        onChange({ target: { name, value } });
      }
    },
    [name, onChange],
  );

  useEffect(() => {
    const editor = quillRef.current?.editor?.root;

    if (editor) {
      editor.addEventListener('paste', handlePaste);
    }

    document.body.addEventListener('keyup', handleTabKeyUp);

    return () => {
      if (editor) {
        editor.removeEventListener('paste', handlePaste);
      }
      document.body.removeEventListener('keyup', handleTabKeyUp);
    };
  }, [handlePaste, handleTabKeyUp]);

  const labelClassName = classNames({ hidden: !showLabel });
  const combinedClassName = classNames(style.editor, className, { changed });

  return (
    <div className={style.host}>
      <label className={labelClassName} htmlFor={id}>
        {label}
      </label>
      <div className={combinedClassName}>
        <ReactQuill
          {...rest}
          ref={quillRef}
          formats={formats}
          modules={modules}
          name={name}
          onChange={handleChange}
          placeholder={placeholder}
          value={text}
        />
      </div>
    </div>
  );
};

Wysiwyg.propTypes = {
  changed: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.any,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  showLabel: PropTypes.bool,
  value: PropTypes.string,
};

export default memo(DeepValue(ChangeDetection(Wysiwyg)));
